/**
 * PLO Enamel Assessment Quiz v1
 * Version: 1.0.0
 * 
 * Features:
 * - 24 questions + name + email capture
 * - 3 reassurance/education panels
 * - 3 commitment gates during loading
 * - Dynamic Enamel Repair Potential score
 * - Personalized results paths (Sensitivity Refugee, Confidence Reclaimer, Gum Health)
 * - Full GA4 + Meta Pixel tracking (every step)
 * - Klaviyo email integration with quiz data properties
 */

(function() {
  'use strict';

  // ==========================================
  // QUIZ ANALYTICS MODULE
  // ==========================================
  const QuizAnalytics = {
    quizName: 'plo_enamel_assessment_v1',
    enableGA4: true,
    enableMeta: true,
    sessionId: null,
    startTime: null,

    init(config) {
      this.quizName = config.quizName || this.quizName;
      this.enableGA4 = config.enableGA4 !== false;
      this.enableMeta = config.enableMeta !== false;
      this.sessionId = this.getOrCreateSessionId();
    },

    getOrCreateSessionId() {
      let sessionId = sessionStorage.getItem('plo_quiz_session');
      if (!sessionId) {
        sessionId = 'ploq_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('plo_quiz_session', sessionId);
      }
      return sessionId;
    },

    getTrafficSource() {
      const params = new URLSearchParams(window.location.search);
      return {
        source: params.get('utm_source') || 'direct',
        medium: params.get('utm_medium') || 'none',
        campaign: params.get('utm_campaign') || 'none',
        content: params.get('utm_content') || '',
        term: params.get('utm_term') || ''
      };
    },

    isDebug() {
      return window.location.hostname === 'localhost' || window.location.search.includes('debug=true');
    },

    /**
     * Core tracking method — fires GA4 + Meta for every event
     */
    track(eventName, eventData) {
      const payload = {
        quiz_name: this.quizName,
        quiz_version: '1.0',
        session_id: this.sessionId,
        ...eventData
      };

      // GA4
      if (this.enableGA4 && typeof gtag === 'function') {
        gtag('event', eventName, payload);
      }

      // Meta Pixel — custom event
      if (this.enableMeta && typeof fbq === 'function') {
        fbq('trackCustom', eventName, payload);
      }

      if (this.isDebug()) {
        console.log('%c[PLO Quiz]', 'color: #2B5EA7; font-weight: bold;', eventName, payload);
      }
    },

    // --- Event-specific methods ---

    trackLandingView() {
      const traffic = this.getTrafficSource();
      this.track('quiz_landing_view', {
        traffic_source: traffic.source,
        traffic_medium: traffic.medium,
        traffic_campaign: traffic.campaign,
        page_url: window.location.pathname
      });
    },

    trackQuizStart() {
      this.startTime = Date.now();
      const traffic = this.getTrafficSource();
      this.track('quiz_start', {
        traffic_source: traffic.source,
        traffic_medium: traffic.medium,
        traffic_campaign: traffic.campaign
      });

      // Meta standard event
      if (this.enableMeta && typeof fbq === 'function') {
        fbq('track', 'ViewContent', {
          content_name: this.quizName,
          content_category: 'Quiz'
        });
      }
    },

    trackQuestionView(questionNumber, questionId, questionType) {
      this.track('quiz_question_view', {
        question_number: questionNumber,
        question_id: questionId,
        question_type: questionType
      });
    },

    trackQuestionAnswer(questionNumber, questionId, answer, timeOnQuestion) {
      const sanitizedAnswer = Array.isArray(answer) 
        ? answer.join(' | ').substring(0, 300)
        : String(answer).substring(0, 300);

      this.track('quiz_question_answer', {
        question_number: questionNumber,
        question_id: questionId,
        answer_value: sanitizedAnswer,
        time_on_question_seconds: Math.round(timeOnQuestion / 1000)
      });
    },

    trackPanelView(panelId, panelType) {
      this.track('quiz_panel_view', {
        panel_id: panelId,
        panel_type: panelType
      });
    },

    trackSegment(segmentKey, segmentValue) {
      this.track('quiz_segment', {
        segment_key: segmentKey,
        segment_value: String(segmentValue).substring(0, 100)
      });

      // Additional dedicated Meta event for audience building
      if (this.enableMeta && typeof fbq === 'function') {
        fbq('trackCustom', 'QuizSegment_' + segmentKey, { 
          value: String(segmentValue).substring(0, 100) 
        });
      }
    },

    trackNameCapture() {
      this.track('quiz_name_capture', {});
    },

    trackEmailCapture(hasMarketingConsent) {
      this.track('quiz_email_capture', {
        marketing_consent: hasMarketingConsent
      });

      // Meta standard Lead event
      if (this.enableMeta && typeof fbq === 'function') {
        fbq('track', 'Lead', { 
          content_name: this.quizName,
          content_category: 'Quiz Email Capture'
        });
      }
    },

    trackCommitmentGate(gateNumber, response) {
      this.track('quiz_commitment_gate', {
        gate_number: gateNumber,
        response: response
      });
    },

    trackQuizComplete(answers, scoreTier, totalTime) {
      this.track('quiz_complete', {
        score_tier: scoreTier,
        readiness: answers.readiness || '',
        interest: answers.interest_level || '',
        proof_type: answers.proof_needed || '',
        aspiration: answers.confident_smile || '',
        total_questions: Object.keys(answers).length,
        total_time_seconds: Math.round(totalTime / 1000)
      });

      // Meta standard CompleteRegistration
      if (this.enableMeta && typeof fbq === 'function') {
        fbq('track', 'CompleteRegistration', {
          content_name: this.quizName,
          status: 'complete',
          value: 0,
          currency: 'USD'
        });
      }

      this.storeForAttribution(answers, scoreTier);
    },

    trackCTAClick(ctaType, scoreTier) {
      this.track('quiz_cta_click', {
        cta_type: ctaType,
        score_tier: scoreTier
      });

      // Meta standard AddToCart
      if (this.enableMeta && typeof fbq === 'function') {
        fbq('track', 'AddToCart', {
          content_name: 'V5 Real White LED Whitening System',
          content_category: 'Quiz Result',
          value: 199.99,
          currency: 'USD'
        });
      }
    },

    trackQuizAbandon(lastQuestionNumber, lastQuestionId, totalTime) {
      this.track('quiz_abandon', {
        last_question_number: lastQuestionNumber,
        last_question_id: lastQuestionId,
        questions_completed: lastQuestionNumber - 1,
        total_time_seconds: Math.round(totalTime / 1000)
      });
    },

    storeForAttribution(answers, scoreTier) {
      const quizData = {
        sessionId: this.sessionId,
        scoreTier: scoreTier,
        readiness: answers.readiness,
        interest: answers.interest_level,
        proofType: answers.proof_needed,
        email: answers.email_capture,
        name: answers.name_capture,
        completedAt: new Date().toISOString()
      };
      
      try {
        localStorage.setItem('plo_quiz_data_v1', JSON.stringify(quizData));
        document.cookie = `plo_quiz_tier=${encodeURIComponent(scoreTier)}; path=/; max-age=2592000`;
      } catch(e) { /* silent */ }
    }
  };


  // ==========================================
  // KLAVIYO INTEGRATION MODULE
  // ==========================================
  const KlaviyoIntegration = {
    listId: null,

    init(listId) {
      this.listId = listId;
    },

    /**
     * Subscribe email to Klaviyo with quiz properties
     */
    subscribe(email, name, answers, scoreTier, consent) {
      // Method 1: Klaviyo Client-side identify + track
      if (typeof _learnq !== 'undefined') {
        // Identify the profile
        _learnq.push(['identify', {
          '$email': email,
          '$first_name': name || '',
          'Quiz Completed': true,
          'Quiz Name': 'PLO Enamel Assessment',
          'Enamel Score Tier': scoreTier,
          'Quiz - Brought Here': answers.brought_here || '',
          'Quiz - Time Thinking': answers.time_thinking || '',
          'Quiz - Smile Feeling': answers.smile_in_photos || '',
          'Quiz - Symptoms': Array.isArray(answers.symptoms_experienced) ? answers.symptoms_experienced.join(', ') : '',
          'Quiz - Sensitivity Freq': answers.sensitivity_frequency || '',
          'Quiz - Staining Habits': Array.isArray(answers.morning_routine) ? answers.morning_routine.join(', ') : '',
          'Quiz - Tongue Test': answers.tongue_test || '',
          'Quiz - Past Whitening': Array.isArray(answers.past_whitening) ? answers.past_whitening.join(', ') : '',
          'Quiz - Why Stopped': Array.isArray(answers.why_stopped) ? answers.why_stopped.join(', ') : '',
          'Quiz - Blame Belief': answers.blame_belief || '',
          'Quiz - Money Spent': answers.money_spent || '',
          'Quiz - Ideal Result': answers.ideal_result || '',
          'Quiz - Dentist Scenario': answers.dentist_scenario || '',
          'Quiz - Confident Smile': answers.confident_smile || '',
          'Quiz - nHAp Awareness': answers.nhap_awareness || '',
          'Quiz - Light Therapy': answers.light_therapy || '',
          'Quiz - Readiness': answers.readiness || '',
          'Quiz - Proof Needed': answers.proof_needed || '',
          'Quiz - Interest Level': answers.interest_level || '',
          'Quiz - Marketing Consent': consent,
          'Quiz Session ID': QuizAnalytics.sessionId,
          'Quiz Completed Date': new Date().toISOString()
        }]);

        // Track the quiz completion event
        _learnq.push(['track', 'Completed Enamel Assessment', {
          'Score Tier': scoreTier,
          'Readiness': answers.readiness || '',
          'Interest': answers.interest_level || '',
          'Primary Concern': answers.brought_here || '',
          'Session ID': QuizAnalytics.sessionId
        }]);
      }

      // Method 2: Klaviyo Back-to-Stock / Subscribe API (list subscription)
      if (this.listId && typeof window._klOnsite !== 'undefined') {
        try {
          window._klOnsite.push(['openForm', 'CUSTOM']);
        } catch(e) { /* silent */ }
      }

      // Method 3: Client-side API call for list subscription
      if (this.listId) {
        this.subscribeToList(email, name, answers, scoreTier);
      }
    },

    subscribeToList(email, name, answers, scoreTier) {
      // Uses Klaviyo's client-side subscribe endpoint
      const companyId = this.getKlaviyoCompanyId();
      if (!companyId || !this.listId) return;

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'PLO Enamel Assessment Quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  first_name: name || '',
                  properties: {
                    'Quiz Score Tier': scoreTier,
                    'Quiz Primary Concern': answers.brought_here || '',
                    'Quiz Readiness': answers.readiness || '',
                    'Quiz Session ID': QuizAnalytics.sessionId
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this.listId
              }
            }
          }
        }
      };

      fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'revision': '2024-02-15'
        },
        body: JSON.stringify(payload)
      }).catch(err => {
        if (QuizAnalytics.isDebug()) console.warn('[PLO Quiz] Klaviyo subscribe error:', err);
      });
    },

    getKlaviyoCompanyId() {
      // Try to extract from existing Klaviyo snippet
      if (typeof _learnq !== 'undefined' && _learnq.account) {
        return _learnq.account;
      }
      // Look for it in the page
      const scripts = document.querySelectorAll('script');
      for (const s of scripts) {
        const match = s.textContent.match(/company_id['":\s]+['"]([A-Za-z0-9]+)['"]/);
        if (match) return match[1];
        const srcMatch = s.src?.match(/company_id=([A-Za-z0-9]+)/);
        if (srcMatch) return srcMatch[1];
      }
      return null;
    }
  };


  // ==========================================
  // ENAMEL SCORE CALCULATOR
  // ==========================================
  const ScoreCalculator = {
    calculate(answers, scoreWeights) {
      let totalWeight = 0;
      let weightedSum = 0;
      let factors = 0;

      // Factor 1: Sensitivity frequency (higher weight)
      if (answers.sensitivity_frequency && scoreWeights.sensitivity_frequency) {
        const val = scoreWeights.sensitivity_frequency[answers.sensitivity_frequency];
        if (val !== undefined) {
          weightedSum += val * 2;
          totalWeight += 2;
          factors++;
        }
      }

      // Factor 2: Tongue test
      if (answers.tongue_test && scoreWeights.tongue_test) {
        const val = scoreWeights.tongue_test[answers.tongue_test];
        if (val !== undefined) {
          weightedSum += val * 1.5;
          totalWeight += 1.5;
          factors++;
        }
      }

      // Factor 3: Time thinking (longer = lower potential)
      if (answers.time_thinking && scoreWeights.time_thinking) {
        const val = scoreWeights.time_thinking[answers.time_thinking];
        if (val !== undefined) {
          weightedSum += val;
          totalWeight += 1;
          factors++;
        }
      }

      // Factor 4: Peroxide exposure (past whitening methods)
      if (Array.isArray(answers.past_whitening)) {
        const peroxideMethods = [
          'Whitening strips (Crest, store brands, etc.)',
          'Professional in-office whitening at the dentist',
          'Custom trays with bleaching gel'
        ];
        const peroxideCount = answers.past_whitening.filter(a => peroxideMethods.includes(a)).length;
        if (peroxideCount === 0) {
          weightedSum += 75;
        } else if (peroxideCount === 1) {
          weightedSum += 55;
        } else {
          weightedSum += 35;
        }
        totalWeight += 1;
        factors++;
      }

      // Factor 5: Symptom count 
      if (Array.isArray(answers.symptoms_experienced)) {
        const hasNone = answers.symptoms_experienced.includes('None of these');
        if (hasNone) {
          weightedSum += 85;
        } else {
          const count = answers.symptoms_experienced.length;
          if (count <= 1) weightedSum += 70;
          else if (count <= 3) weightedSum += 50;
          else weightedSum += 30;
        }
        totalWeight += 1;
        factors++;
      }

      // Factor 6: Staining habits
      if (Array.isArray(answers.morning_routine)) {
        const hasNone = answers.morning_routine.includes('None of these');
        if (hasNone) {
          weightedSum += 80;
        } else {
          const count = answers.morning_routine.length;
          if (count <= 1) weightedSum += 65;
          else if (count <= 3) weightedSum += 50;
          else weightedSum += 35;
        }
        totalWeight += 0.5;
        factors++;
      }

      if (totalWeight === 0) return { score: 65, tier: 'MODERATE-HIGH' };

      let score = Math.round(weightedSum / totalWeight);

      // Clamp between 38 and 92 so no one gets a discouraging or unrealistically high score
      score = Math.max(38, Math.min(92, score));

      let tier;
      if (score >= 70) tier = 'HIGH';
      else if (score >= 50) tier = 'MODERATE-HIGH';
      else tier = 'MODERATE';

      return { score, tier };
    }
  };


  // ==========================================
  // QUIZ UI MODULE
  // ==========================================
  class PLOEnamelQuiz {
    constructor(container) {
      this.container = container;
      this.config = this.loadConfig();
      this.currentStep = 0;
      this.answers = {};
      this.questionStartTime = Date.now();
      this.quizStartTime = null;
      this.completed = false;
      this.emailCaptured = false;
      this.commitmentResponses = {};
      this.userName = '';
      this.scoreTier = '';
      this.scoreValue = 0;

      this.elements = {
        landingScreen: container.querySelector('[data-landing-screen]'),
        startBtn: container.querySelector('[data-start-quiz]'),
        quizContainer: container.querySelector('[data-quiz-container]'),
        questionContainer: container.querySelector('[data-question-content]'),
        progressFill: container.querySelector('[data-progress-fill]'),
        currentStepEl: container.querySelector('[data-current-step]'),
        totalStepsEl: container.querySelector('[data-total-steps]'),
        backBtn: container.querySelector('[data-action="back"]'),
        continueBtn: container.querySelector('[data-continue-btn]'),
        loadingScreen: container.querySelector('[data-loading-screen]'),
        loadingMain: container.querySelector('[data-loading-main]'),
        loadingProgress: container.querySelector('[data-loading-progress]'),
        loadingStatus: container.querySelector('[data-loading-status]'),
        commitment1: container.querySelector('[data-commitment-1]'),
        commitment2: container.querySelector('[data-commitment-2]'),
        commitment3: container.querySelector('[data-commitment-3]'),
        resultsScreen: container.querySelector('[data-results-screen]')
      };

      this.init();
    }

    loadConfig() {
      const configEl = document.querySelector('[data-quiz-config]');
      if (configEl) {
        try {
          return JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('Failed to parse quiz config:', e);
        }
      }
      return { questions: [], scoreWeights: {}, paths: {}, testimonials: [], offer: {} };
    }

    saveProgress() {
      try {
        sessionStorage.setItem('plo_quiz_progress', JSON.stringify({
          step: this.currentStep,
          answers: this.answers,
          userName: this.userName,
          startTime: this.quizStartTime
        }));
      } catch(e) { /* silent */ }
    }

    restoreProgress() {
      try {
        const saved = JSON.parse(sessionStorage.getItem('plo_quiz_progress'));
        if (saved && saved.step > 0) {
          this.currentStep = saved.step;
          this.answers = saved.answers || {};
          this.userName = saved.userName || '';
          this.quizStartTime = saved.startTime || Date.now();
          return true;
        }
      } catch(e) { /* silent */ }
      return false;
    }

    clearProgress() {
      try { sessionStorage.removeItem('plo_quiz_progress'); } catch(e) {}
    }

    init() {
      QuizAnalytics.init({
        quizName: this.container.dataset.quizName,
        enableGA4: this.container.dataset.enableGa4 !== 'false',
        enableMeta: this.container.dataset.enableMeta !== 'false'
      });

      KlaviyoIntegration.init(this.container.dataset.klaviyoList || '');

      // Count actual questions (excluding panels)
      this.questionCount = this.config.questions.filter(q => q.type !== 'panel').length;
      this.elements.totalStepsEl.textContent = this.questionCount;

      this.bindEvents();
      QuizAnalytics.trackLandingView();
      window.addEventListener('beforeunload', () => this.handleAbandon());

      // Auto-resume if saved progress exists
      if (this.restoreProgress()) {
        this.elements.landingScreen.style.display = 'none';
        this.elements.quizContainer.style.display = 'block';
        this.quizStartTime = this.quizStartTime || Date.now();
        this.renderQuestion();
      }
    }

    bindEvents() {
      this.elements.startBtn.addEventListener('click', () => this.startQuiz());
      this.elements.backBtn.addEventListener('click', () => this.goBack());
      this.elements.continueBtn.addEventListener('click', () => this.goNext());

      // Commitment gate buttons
      this.container.querySelectorAll('[data-commitment-answer]').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleCommitmentAnswer(e));
      });
    }

    startQuiz() {
      this.quizStartTime = Date.now();
      this.elements.landingScreen.style.display = 'none';
      this.elements.quizContainer.style.display = 'block';
      QuizAnalytics.trackQuizStart();

      if (this.restoreProgress()) {
        this.renderQuestion();
      } else {
        this.renderQuestion();
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    getVisualStepNumber() {
      let count = 0;
      for (let i = 0; i <= this.currentStep; i++) {
        if (this.config.questions[i] && this.config.questions[i].type !== 'panel') {
          count++;
        }
      }
      return count;
    }

    renderQuestion() {
      const question = this.config.questions[this.currentStep];
      if (!question) return;

      this.updateProgress();
      this.questionStartTime = Date.now();

      // Track view
      if (question.type === 'panel') {
        QuizAnalytics.trackPanelView(question.id, question.panelType);
      } else {
        QuizAnalytics.trackQuestionView(this.getVisualStepNumber(), question.id, question.type);
      }

      // Show/hide back button
      this.elements.backBtn.style.visibility = this.currentStep > 0 ? 'visible' : 'hidden';

      // Render based on type
      let html = '';
      switch (question.type) {
        case 'single': html = this.renderSingleSelect(question); break;
        case 'multi': html = this.renderMultiSelect(question); break;
        case 'panel': html = this.renderPanel(question); break;
        case 'name': html = this.renderNameCapture(question); break;
        case 'email': html = this.renderEmailCapture(question); break;
      }

      this.elements.questionContainer.innerHTML = html;
      this.animateIn();
      this.bindOptionEvents(question);
      this.updateContinueButton(question);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    renderSingleSelect(question) {
      return `
        <h2 class="plo-quiz__question-title">${question.question}</h2>
        ${question.subtitle ? `<p class="plo-quiz__question-subtitle">${this.escapeHtml(question.subtitle)}</p>` : ''}
        <div class="plo-quiz__options">
          ${question.options.map((option, i) => `
            <button class="plo-quiz__option" data-option-index="${i}" data-option-value="${this.escapeHtml(option)}">
              ${this.escapeHtml(option)}
            </button>
          `).join('')}
        </div>
      `;
    }

    renderMultiSelect(question) {
      const selected = this.answers[question.id] || [];
      return `
        <h2 class="plo-quiz__question-title">${question.question}</h2>
        ${question.subtitle ? `<p class="plo-quiz__question-subtitle">${this.escapeHtml(question.subtitle)}</p>` : ''}
        ${question.instruction ? `<p class="plo-quiz__question-instruction">${this.escapeHtml(question.instruction)}</p>` : ''}
        <div class="plo-quiz__options">
          ${question.options.map((option, i) => `
            <button class="plo-quiz__option ${selected.includes(option) ? 'plo-quiz__option--selected' : ''}" 
                    data-option-index="${i}" data-option-value="${this.escapeHtml(option)}" data-multi="true">
              <span class="plo-quiz__checkbox"><span class="plo-quiz__checkbox-icon">✓</span></span>
              ${this.escapeHtml(option)}
            </button>
          `).join('')}
        </div>
      `;
    }

    renderPanel(question) {
      let contentHtml = `<div class="plo-quiz__panel-content">${question.content}</div>`;
      
      if (question.conclusion) {
        contentHtml = `
          <div class="plo-quiz__mechanism-card">
            <div>${question.content}</div>
            <div class="plo-quiz__mechanism-conclusion">${question.conclusion}</div>
          </div>
        `;
      }

      return `
        <div class="plo-quiz__panel">
          <h2 class="plo-quiz__panel-title">${question.title}</h2>
          ${contentHtml}
          <button class="plo-quiz__panel-btn" data-panel-continue>${question.button || 'CONTINUE'}</button>
        </div>
      `;
    }

    renderNameCapture(question) {
      return `
        <div class="plo-quiz__panel">
          <h2 class="plo-quiz__question-title">${question.question}</h2>
          <p class="plo-quiz__question-subtitle">${this.escapeHtml(question.subtitle)}</p>
          <div class="plo-quiz__name-form">
            <input type="text" class="plo-quiz__name-input" placeholder="${this.escapeHtml(question.placeholder)}" data-name-input autocomplete="given-name" maxlength="50">
            <button class="plo-quiz__name-btn" data-name-submit>${question.button || 'CONTINUE'}</button>
          </div>
        </div>
      `;
    }

    renderEmailCapture(question) {
      return `
        <div class="plo-quiz__panel">
          <h2 class="plo-quiz__question-title">${question.question}</h2>
          <p class="plo-quiz__question-subtitle">${question.subtitle}</p>
          <div class="plo-quiz__email-form">
            <input type="email" class="plo-quiz__email-input" placeholder="${this.escapeHtml(question.placeholder)}" data-email-input required autocomplete="email">
            <p class="plo-quiz__email-privacy" style="margin-top: 16px; font-size: 13px; color: var(--plo-charcoal-light); line-height: 1.5;">${question.checkboxLabel}</p>
            <button class="plo-quiz__email-btn" data-email-submit>${question.button}</button>
            <p class="plo-quiz__email-privacy">${question.privacyText}</p>
          </div>
        </div>
      `;
    }

    bindOptionEvents(question) {
      if (question.type === 'single') {
        this.elements.questionContainer.querySelectorAll('.plo-quiz__option').forEach(btn => {
          btn.addEventListener('click', () => this.selectOption(question, btn.dataset.optionValue));
        });
      }

      if (question.type === 'multi') {
        this.elements.questionContainer.querySelectorAll('.plo-quiz__option').forEach(btn => {
          btn.addEventListener('click', () => this.toggleMultiOption(question, btn));
        });
      }

      if (question.type === 'panel') {
        const panelBtn = this.elements.questionContainer.querySelector('[data-panel-continue]');
        if (panelBtn) {
          panelBtn.addEventListener('click', () => this.goNext());
        }
      }

      if (question.type === 'name') {
        const nameBtn = this.elements.questionContainer.querySelector('[data-name-submit]');
        const nameInput = this.elements.questionContainer.querySelector('[data-name-input]');
        
        if (nameBtn && nameInput) {
          nameBtn.addEventListener('click', () => this.handleNameSubmit());
          nameInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') this.handleNameSubmit();
          });
          // Auto-focus
          setTimeout(() => nameInput.focus(), 300);
        }
      }

      if (question.type === 'email') {
        const emailBtn = this.elements.questionContainer.querySelector('[data-email-submit]');
        const emailInput = this.elements.questionContainer.querySelector('[data-email-input]');
        
        if (emailBtn && emailInput) {
          emailBtn.addEventListener('click', () => this.handleEmailSubmit());
          emailInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') this.handleEmailSubmit();
          });
          setTimeout(() => emailInput.focus(), 300);
        }
      }
    }

    selectOption(question, value) {
      this.elements.questionContainer.querySelectorAll('.plo-quiz__option').forEach(btn => {
        btn.classList.toggle('plo-quiz__option--selected', btn.dataset.optionValue === value);
      });

      this.answers[question.id] = value;
      
      const timeOnQuestion = Date.now() - this.questionStartTime;
      QuizAnalytics.trackQuestionAnswer(this.getVisualStepNumber(), question.id, value, timeOnQuestion);

      if (question.segmentKey) {
        QuizAnalytics.trackSegment(question.segmentKey, value);
      }

      setTimeout(() => this.goNext(), 350);
      this.saveProgress();
    }

    toggleMultiOption(question, btn) {
      const value = btn.dataset.optionValue;
      let selected = this.answers[question.id] || [];

      if (selected.includes(value)) {
        selected = selected.filter(v => v !== value);
        btn.classList.remove('plo-quiz__option--selected');
      } else {
        selected.push(value);
        btn.classList.add('plo-quiz__option--selected');
      }

      this.answers[question.id] = selected;
      this.updateContinueButton(question);
      this.saveProgress();
    }

    handleNameSubmit() {
      const nameInput = this.elements.questionContainer.querySelector('[data-name-input]');
      const name = nameInput?.value?.trim();

      if (!name) {
        nameInput.style.borderColor = '#C23B22';
        nameInput.focus();
        return;
      }

      this.userName = name;
      this.answers.name_capture = name;

      const timeOnQuestion = Date.now() - this.questionStartTime;
      QuizAnalytics.trackNameCapture();
      QuizAnalytics.trackQuestionAnswer(this.getVisualStepNumber(), 'name_capture', '[name]', timeOnQuestion);

      this.goNext();
    }

    handleEmailSubmit() {
      const emailInput = this.elements.questionContainer.querySelector('[data-email-input]');
      const consentBox = this.elements.questionContainer.querySelector('[data-marketing-consent]');
      const email = emailInput?.value?.trim();

      if (!email || !this.isValidEmail(email)) {
        emailInput.style.borderColor = '#C23B22';
        emailInput.focus();
        return;
      }

      this.answers.email_capture = email;
      this.answers.marketing_consent = consentBox?.checked || false;
      this.emailCaptured = true;

      QuizAnalytics.trackEmailCapture(this.answers.marketing_consent);
      
      const question = this.config.questions[this.currentStep];
      const timeOnQuestion = Date.now() - this.questionStartTime;
      QuizAnalytics.trackQuestionAnswer(this.getVisualStepNumber(), question.id, '[email]', timeOnQuestion);

      // Calculate score before loading
      const { score, tier } = ScoreCalculator.calculate(this.answers, this.config.scoreWeights);
      this.scoreValue = score;
      this.scoreTier = tier;

      // Send to Klaviyo
      KlaviyoIntegration.subscribe(
        email, 
        this.userName, 
        this.answers, 
        tier, 
        this.answers.marketing_consent
      );

      this.clearProgress();
      this.showLoading();
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    updateContinueButton(question) {
      if (question.type === 'multi') {
        const selected = this.answers[question.id] || [];
        const minSelections = question.minSelections || 1;
        this.elements.continueBtn.style.display = 'block';
        const hasEnough = selected.length >= minSelections;
        this.elements.continueBtn.classList.toggle('plo-quiz__continue-btn--active', hasEnough);
        this.elements.continueBtn.disabled = !hasEnough;
        this.elements.continueBtn.setAttribute('aria-disabled', !hasEnough);
      } else {
        this.elements.continueBtn.style.display = 'none';
      }
    }

    updateProgress() {
      const visualStep = this.getVisualStepNumber();
      const progress = (visualStep / this.questionCount) * 100;
      this.elements.progressFill.style.width = `${progress}%`;
      this.elements.currentStepEl.textContent = visualStep;
    }

    animateIn() {
      const container = this.elements.questionContainer.parentElement;
      container.style.animation = 'none';
      container.offsetHeight; // reflow
      container.style.animation = 'plo-slideIn 0.35s ease-out';
    }

    goNext() {
      const question = this.config.questions[this.currentStep];
      
      // Track multi-select answer on continue
      if (question.type === 'multi') {
        const timeOnQuestion = Date.now() - this.questionStartTime;
        QuizAnalytics.trackQuestionAnswer(
          this.getVisualStepNumber(), 
          question.id, 
          this.answers[question.id] || [], 
          timeOnQuestion
        );

        if (question.segmentKey) {
          const val = (this.answers[question.id] || []).join(' | ');
          QuizAnalytics.trackSegment(question.segmentKey, val);
        }
      }

      if (this.currentStep < this.config.questions.length - 1) {
        this.currentStep++;
        this.renderQuestion();
      }
    }

    goBack() {
      if (this.currentStep > 0) {
        this.currentStep--;
        this.renderQuestion();
      }
    }

    handleAbandon() {
      if (!this.completed && this.quizStartTime && this.currentStep > 0) {
        const question = this.config.questions[this.currentStep];
        const totalTime = Date.now() - this.quizStartTime;
        QuizAnalytics.trackQuizAbandon(this.getVisualStepNumber(), question?.id, totalTime);
      }
    }


    // ==========================================
    // LOADING SCREEN + COMMITMENT GATES
    // ==========================================

    showLoading() {
      this.elements.quizContainer.style.display = 'none';
      this.elements.loadingScreen.style.display = 'flex';
      this.elements.loadingMain.style.display = 'block';
      this.elements.commitment1.style.display = 'none';
      this.elements.commitment2.style.display = 'none';
      this.elements.commitment3.style.display = 'none';

      // Inject user name into commitment gate 2
      const nameDisplay = this.elements.commitment2.querySelector('[data-user-name-display]');
      if (nameDisplay) nameDisplay.textContent = this.userName || '';

      window.scrollTo({ top: 0, behavior: 'smooth' });

      let progress = 0;
      let currentCheck = 0;

      const progressInterval = setInterval(() => {
        progress += 1.2;
        this.elements.loadingProgress.style.width = `${Math.min(progress, 100)}%`;

        // Update checklist
        if (progress >= 30 && currentCheck === 0) {
          this.completeCheck('symptoms');
          currentCheck = 1;
        }
        if (progress >= 60 && currentCheck === 1) {
          this.completeCheck('causes');
          currentCheck = 2;
        }
        if (progress >= 90 && currentCheck === 2) {
          this.completeCheck('recommendations');
          currentCheck = 3;
        }

        // Commitment gate 1 at ~30%
        if (progress >= 30 && progress < 33 && !this.commitmentResponses[1]) {
          clearInterval(progressInterval);
          this.showCommitmentGate(1);
          return;
        }

        // Commitment gate 2 at ~60%
        if (progress >= 60 && progress < 63 && !this.commitmentResponses[2]) {
          clearInterval(progressInterval);
          this.showCommitmentGate(2);
          return;
        }

        // Commitment gate 3 at ~90%
        if (progress >= 90 && progress < 93 && !this.commitmentResponses[3]) {
          clearInterval(progressInterval);
          this.showCommitmentGate(3);
          return;
        }

        if (progress >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => this.showResults(), 500);
        }
      }, 80);

      this._progressInterval = progressInterval;
    }

    completeCheck(checkId) {
      const check = this.container.querySelector(`[data-check="${checkId}"]`);
      if (check) {
        check.classList.add('plo-quiz__loading-check--complete');
        check.querySelector('.plo-quiz__loading-check-icon').textContent = '✓';
      }
    }

    showCommitmentGate(gateNumber) {
      this.elements.loadingMain.style.display = 'none';
      const gateEl = this.container.querySelector(`[data-commitment-${gateNumber}]`);
      if (gateEl) {
        gateEl.style.display = 'block';
        gateEl.style.animation = 'plo-fadeUp 0.4s ease-out';
      }
    }

    handleCommitmentAnswer(e) {
      const btn = e.target.closest('[data-commitment-answer]');
      if (!btn) return;

      const response = btn.dataset.commitmentAnswer;
      const gateNumber = parseInt(btn.dataset.gate);

      this.commitmentResponses[gateNumber] = response;
      QuizAnalytics.trackCommitmentGate(gateNumber, response);

      // Hide gate, resume loading
      const gateEl = this.container.querySelector(`[data-commitment-${gateNumber}]`);
      if (gateEl) gateEl.style.display = 'none';
      this.elements.loadingMain.style.display = 'block';

      // Resume progress
      let progress = gateNumber === 1 ? 33 : gateNumber === 2 ? 63 : 93;
      let currentCheck = gateNumber; // already completed that many checks

      const progressInterval = setInterval(() => {
        progress += 1.2;
        this.elements.loadingProgress.style.width = `${Math.min(progress, 100)}%`;

        if (progress >= 60 && currentCheck === 1) {
          this.completeCheck('causes');
          currentCheck = 2;
        }
        if (progress >= 90 && currentCheck === 2) {
          this.completeCheck('recommendations');
          currentCheck = 3;
        }

        // Next commitment gate
        if (progress >= 60 && progress < 63 && !this.commitmentResponses[2]) {
          clearInterval(progressInterval);
          this.showCommitmentGate(2);
          return;
        }
        if (progress >= 90 && progress < 93 && !this.commitmentResponses[3]) {
          clearInterval(progressInterval);
          this.showCommitmentGate(3);
          return;
        }

        if (progress >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => this.showResults(), 500);
        }
      }, 80);
    }


    // ==========================================
    // RESULTS PAGE
    // ==========================================

    showResults() {
      this.completed = true;
      this.elements.loadingScreen.style.display = 'none';
      this.elements.resultsScreen.style.display = 'block';
      this.elements.resultsScreen.innerHTML = this.buildResultsHTML();
      this.bindResultsEvents();

      const totalTime = Date.now() - (this.quizStartTime || Date.now());
      QuizAnalytics.trackQuizComplete(this.answers, this.scoreTier, totalTime);

      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Animate score gauge after render
      setTimeout(() => this.animateGauge(), 400);
    }

    determinePath() {
      const whyStopped = this.answers.why_stopped || [];
      const smileFeeling = this.answers.smile_in_photos || '';
      const confidentSmile = this.answers.confident_smile || '';
      const symptoms = this.answers.symptoms_experienced || [];
      const lightTherapy = this.answers.light_therapy || '';

      // Sensitivity Refugee
      if (whyStopped.includes("The sensitivity was unbearable")) {
        return 'sensitivity_refugee';
      }

      // Gum Health
      if (symptoms.includes("Gums that bleed when you brush or floss") || 
          lightTherapy.includes("it's part of why I'm interested")) {
        return 'gum_health';
      }

      // Confidence Reclaimer
      if (smileFeeling.includes("self-conscious") || 
          smileFeeling.includes("avoid looking") ||
          confidentSmile.includes("feel like myself again") ||
          confidentSmile.includes("change everything")) {
        return 'confidence_reclaimer';
      }

      return 'sensitivity_refugee'; // default
    }

    getScoreInterpretation() {
      const name = this.userName || '';
      
      if (this.scoreTier === 'HIGH') {
        return `Great news, ${name}. Despite what you've been through, your teeth show strong repair potential. The sensitivity and discoloration you've experienced are likely surface-level enamel damage that responds well to mineral-based rebuilding. With the right approach, you could see meaningful improvement within the first few weeks.`;
      }
      if (this.scoreTier === 'MODERATE-HIGH') {
        return `${name}, your results suggest your enamel has been through some damage — likely from the peroxide-based products you mentioned — but there's real potential for repair. Your sensitivity patterns and lifestyle factors indicate you're a strong candidate for a gentler, rebuilding approach.`;
      }
      return `${name}, based on your responses, your enamel has experienced significant stress. The good news? Even teeth with accumulated damage can respond to the right approach. You may need a bit more time to see results, but the trajectory can absolutely change.`;
    }

    getPathContent() {
      const path = this.determinePath();
      
      if (path === 'sensitivity_refugee') {
        return {
          title: 'Why the V5 Real White System is built for exactly your situation.',
          body: `You mentioned that sensitivity made you stop whitening. Here's why that won't happen with this approach:<br><br>The V5 system uses <strong>nano-hydroxyapatite</strong> — the same mineral that makes up 97% of your tooth enamel. Instead of stripping enamel to bleach stains (which is why peroxide hurts), hydroxyapatite <strong>deposits minerals INTO your enamel</strong>, filling microscopic cracks and creating a smoother, more reflective surface.<br><br>Clinical data: Only <strong>29% of nHAp users experienced any sensitivity</strong> — compared to <strong>51% with conventional whitening</strong>. That's a 43% reduction in sensitivity risk.<br><br><strong>This is whitening that heals. Not whitening that hurts.</strong>`
        };
      }

      if (path === 'gum_health') {
        return {
          title: 'Why the V5 Real White System addresses your whole-mouth concerns.',
          body: `You mentioned concerns about gum health. Here's something most whitening products ignore entirely:<br><br>The V5 system includes <strong class="plo-red-highlight">red light therapy at 660nm wavelength</strong> — the same wavelength used in clinical studies showing reduced gum inflammation and accelerated tissue healing.<br><br>While the <span class="plo-blue-highlight">blue light works on surface staining</span>, the <span class="plo-red-highlight">red light works on the tissue that holds your teeth in place</span>. You're not just whitening — you're supporting your whole mouth.`
        };
      }

      // confidence_reclaimer
      return {
        title: 'Why the V5 Real White System is built for exactly your situation.',
        body: `You mentioned wanting to feel confident in photos again — to smile without strategizing or hiding.<br><br>Here's what makes this different: Traditional whitening gives you a temporary brightness by damaging your teeth. The V5 system gives you lasting brightness by <strong>repairing your teeth</strong>.<br><br>The dual-light technology — <span class="plo-blue-highlight">blue light for whitening activation</span>, <span class="plo-red-highlight">red light for gum tissue healing</span> — works WITH your biology instead of against it. Your teeth get whiter because they're getting healthier.<br><br><strong>Imagine walking into your next dental appointment and hearing, "Whatever you're doing, keep doing it." That's what real users report.</strong>`
      };
    }

    buildResultsHTML() {
      const name = this.userName || '';
      const score = this.scoreValue;
      const tier = this.scoreTier;
      const interpretation = this.getScoreInterpretation();
      const pathContent = this.getPathContent();
      const testimonials = this.config.testimonials || [];
      const offer = this.config.offer || {};

      const tierClass = tier === 'HIGH' ? 'high' : tier === 'MODERATE-HIGH' ? 'moderate-high' : 'moderate';
      
      // SVG gauge math
      const radius = 72;
      const circumference = 2 * Math.PI * radius;
      const dashOffset = circumference - (score / 100) * circumference;
      const gaugeColor = tier === 'HIGH' ? 'var(--plo-success)' : tier === 'MODERATE-HIGH' ? 'var(--plo-blue)' : 'var(--plo-red)';

      return `
        <!-- Header -->
        <div class="plo-quiz__results-header">
          <p class="plo-quiz__results-label">YOUR ENAMEL REPAIR ASSESSMENT</p>
          <h1 class="plo-quiz__results-name">${this.escapeHtml(name)}, here's your Enamel Repair Assessment.</h1>
          <p class="plo-quiz__results-subtitle">Based on your responses, we've calculated your Enamel Repair Potential and identified the specific approach that matches your situation.</p>
        </div>

        <!-- Score -->
        <div class="plo-quiz__results-score">
          <p class="plo-quiz__results-score-label">YOUR ENAMEL REPAIR POTENTIAL</p>
          <div class="plo-quiz__results-gauge">
            <svg viewBox="0 0 160 160">
              <circle class="plo-quiz__results-gauge-bg" cx="80" cy="80" r="${radius}"/>
              <circle class="plo-quiz__results-gauge-fill" cx="80" cy="80" r="${radius}" 
                stroke="${gaugeColor}"
                stroke-dasharray="${circumference}" 
                stroke-dashoffset="${circumference}"
                data-target-offset="${dashOffset}"/>
            </svg>
            <div class="plo-quiz__results-gauge-text">
              <span class="plo-quiz__results-gauge-percent" data-target-score="${score}">0%</span>
              <span class="plo-quiz__results-gauge-tier plo-quiz__results-gauge-tier--${tierClass}">${tier}</span>
            </div>
          </div>
          <p class="plo-quiz__results-score-interpretation">${interpretation}</p>
        </div>

        <div class="plo-quiz__results-content">

          <!-- Personalized Path -->
          <div class="plo-quiz__results-card">
            <h2 class="plo-quiz__results-card-title">${pathContent.title}</h2>
            <p>${pathContent.body}</p>
          </div>

          <!-- Product Features -->
          <div class="plo-quiz__results-card">
            <h2 class="plo-quiz__results-card-title">Introducing the V5 Real White LED Teeth Whitening System</h2>
            <p><strong>The only whitening system that rebuilds enamel while it whitens.</strong></p>
            <div class="plo-quiz__results-features">
              <div class="plo-quiz__results-feature">
                <span class="plo-quiz__results-feature-check">✓</span>
                <span><strong>Nano-Hydroxyapatite Whitening Gel</strong> — The same mineral your teeth are made of. Deposits INTO enamel instead of stripping it away.</span>
              </div>
              <div class="plo-quiz__results-feature">
                <span class="plo-quiz__results-feature-check">✓</span>
                <span><strong>32 Dual-Wavelength LED Lamps</strong> — <span class="plo-blue-highlight">Blue light (480nm)</span> for whitening activation. <span class="plo-red-highlight">Red light (660nm)</span> for gum tissue support.</span>
              </div>
              <div class="plo-quiz__results-feature">
                <span class="plo-quiz__results-feature-check">✓</span>
                <span><strong>Zero Sensitivity Formula</strong> — No peroxide. No bleach. No "zingers."</span>
              </div>
              <div class="plo-quiz__results-feature">
                <span class="plo-quiz__results-feature-check">✓</span>
                <span><strong>16-Minute Sessions</strong> — Use while doing dishes, watching TV, or getting ready for bed.</span>
              </div>
              <div class="plo-quiz__results-feature">
                <span class="plo-quiz__results-feature-check">✓</span>
                <span><strong>UV Sanitizing Charging Case</strong> — Kills 99% of bacteria between sessions (including cavity-causing S. mutans).</span>
              </div>
              <div class="plo-quiz__results-feature">
                <span class="plo-quiz__results-feature-check">✓</span>
                <span><strong>15+ Year Device Lifespan</strong> — One purchase. Years of use.</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Testimonials -->
        <div class="plo-quiz__results-testimonials">
          <h3 class="plo-quiz__results-testimonials-title">What happens when teeth finally get what they need:</h3>
          ${testimonials.map(t => `
            <div class="plo-quiz__results-testimonial">
              <p class="plo-quiz__results-testimonial-quote">"${this.escapeHtml(t.quote)}"</p>
              <p class="plo-quiz__results-testimonial-author">— ${this.escapeHtml(t.author)}${t.age ? ', ' + this.escapeHtml(t.age) : ''}</p>
            </div>
          `).join('')}
        </div>

        <div class="plo-quiz__results-content">
          <!-- Offer -->
          <div class="plo-quiz__results-offer">
            <h2 class="plo-quiz__results-offer-headline">${this.escapeHtml(offer.headline || '')}</h2>
            <p class="plo-quiz__results-offer-product">The V5 Real White System includes:</p>
            
            <div class="plo-quiz__results-offer-items">
              ${(offer.items || []).map(item => `
                <div class="plo-quiz__results-offer-item">
                  <span class="plo-quiz__results-offer-item-icon">✓</span>
                  <span>${this.escapeHtml(item.name)}</span>
                </div>
              `).join('')}
            </div>
            
            <div class="plo-quiz__results-offer-pricing">
              <span class="plo-quiz__results-offer-retail">${this.escapeHtml(offer.retailValue || '')}</span>
              <span class="plo-quiz__results-offer-price">${this.escapeHtml(offer.price || '')}</span>
            </div>
            <span class="plo-quiz__results-offer-savings">SAVE ${this.escapeHtml(offer.discount || '')}%</span>
            
            <button class="plo-quiz__results-offer-cta" data-cta-main>${this.escapeHtml(offer.cta || 'GET MY V5 SYSTEM')} →</button>
            
            <div class="plo-quiz__results-offer-guarantees">
              ${(offer.guarantees || []).map(g => `<span>✓ ${this.escapeHtml(g)}</span>`).join('')}
            </div>
          </div>
        </div>

        <!-- Guarantee -->
        <div class="plo-quiz__results-guarantee">
          <h3 class="plo-quiz__results-guarantee-title">${this.escapeHtml(offer.guaranteeHeadline || '')}</h3>
          <p class="plo-quiz__results-guarantee-body">${(offer.guaranteeBody || '').replace(/\n/g, '<br>')}</p>
          <button class="plo-quiz__results-offer-cta" data-cta-guarantee>${this.escapeHtml(offer.cta || 'GET MY V5 SYSTEM')} →</button>
        </div>

        <!-- Final CTA -->
        <div class="plo-quiz__results-final">
          <button class="plo-quiz__results-final-cta" data-cta-final>${this.escapeHtml(offer.finalCta || 'GET MY V5 SYSTEM')} →</button>
          <div class="plo-quiz__results-final-trust">
            <span>✓ 60-Day Guarantee</span>
            <span>✓ 1-Year Warranty</span>
            <span>✓ Free Shipping</span>
          </div>
        </div>
      `;
    }

    animateGauge() {
      const fillEl = this.elements.resultsScreen.querySelector('.plo-quiz__results-gauge-fill');
      const percentEl = this.elements.resultsScreen.querySelector('.plo-quiz__results-gauge-percent');
      
      if (fillEl) {
        const targetOffset = fillEl.getAttribute('data-target-offset');
        fillEl.style.strokeDashoffset = targetOffset;
      }

      if (percentEl) {
        const targetScore = parseInt(percentEl.getAttribute('data-target-score'));
        let current = 0;
        const duration = 1200;
        const startTime = Date.now();
        
        const animateCount = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          // Ease out
          const eased = 1 - Math.pow(1 - progress, 3);
          current = Math.round(targetScore * eased);
          percentEl.textContent = current + '%';
          
          if (progress < 1) {
            requestAnimationFrame(animateCount);
          }
        };
        
        requestAnimationFrame(animateCount);
      }
    }

    bindResultsEvents() {
      const ctaButtons = this.elements.resultsScreen.querySelectorAll('[data-cta-main], [data-cta-guarantee], [data-cta-final]');
      
      ctaButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const ctaType = btn.hasAttribute('data-cta-main') ? 'main_cta' 
                       : btn.hasAttribute('data-cta-guarantee') ? 'guarantee_cta' 
                       : 'final_cta';
          
          QuizAnalytics.trackCTAClick(ctaType, this.scoreTier);
          
          if (this.config.variantId) {
            this.addToCart(this.config.variantId);
          } else if (this.config.productHandle) {
            window.location.href = `/products/${this.config.productHandle}`;
          }
        });
      });
    }

    addToCart(variantId) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              '_quiz_score_tier': this.scoreTier,
              '_quiz_score': this.scoreValue,
              '_quiz_path': this.determinePath(),
              '_quiz_session': QuizAnalytics.sessionId,
              '_quiz_version': '1.0'
            }
          }]
        })
      })
      .then(res => res.json())
      .then(() => window.location.href = '/cart')
      .catch(() => {
        window.location.href = `/products/${this.config.productHandle}`;
      });
    }

    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }


  // ==========================================
  // INITIALIZATION
  // ==========================================
  function initQuiz() {
    document.querySelectorAll('#plo-quiz').forEach(container => {
      new PLOEnamelQuiz(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuiz);
  } else {
    initQuiz();
  }

  // Expose for debugging
  window.PLOEnamelQuiz = PLOEnamelQuiz;
  window.PLOQuizAnalytics = QuizAnalytics;
})();
